/**
 * AI Acquisition OS — Builder-facing copy (manual preview only).
 *
 * First principles:
 *   - The Builder is a coach, expert, or business owner. Not an engineer.
 *   - Stress-tested against Susanna (Clinic Marketing OS for MSK clinics) and
 *     Phil (PJPT — personal trainer): every line must work for both.
 *   - No jargon. Action-oriented stage names. Coach-friendly states.
 *   - The internal eight-slice contract is preserved as `internalKey` for
 *     downstream code that needs the original mapping.
 *
 * Backing source for the *internal* contract (not shown to Builders):
 *   - docs/plans/2026-05-01-ai-acquisition-os-fixture-dry-run-suite-report.md
 *   - docs/plans/2026-04-30-ai-acquisition-os-v1-meta-operator-product-spine.md
 */

export type StageState = 'done' | 'current' | 'upcoming';

export interface AcquisitionStage {
  /** Builder-facing display number (1-8). */
  num: '01' | '02' | '03' | '04' | '05' | '06' | '07' | '08';
  /** Short, action-oriented Builder-facing name. */
  name: string;
  /** One-line Builder outcome — must work for clinic owners + personal trainers + course creators. */
  description: string;
  /** Coach-friendly state. */
  state: StageState;
  /** Estimated time. */
  duration: string;
  /** Internal slice key — preserved for downstream pipeline; never shown to Builders. */
  internalKey:
    | 'readiness'
    | 'route'
    | 'radar'
    | 'creative'
    | 'campaign-proposal'
    | 'launch'
    | 'signal-loop'
    | 'leak-review';
}

export const ACQUISITION_STAGES: AcquisitionStage[] = [
  {
    num: '01',
    name: 'Start Line',
    description: 'Sharpen your offer, your buyer, and the proof you already have.',
    state: 'done',
    duration: '~25 min',
    internalKey: 'readiness',
  },
  {
    num: '02',
    name: 'Channel',
    description: 'Pick the right way to bring buyers to your offer.',
    state: 'current',
    duration: '~30 min',
    internalKey: 'route',
  },
  {
    num: '03',
    name: 'Research',
    description: 'See what is already working in your market and what is not.',
    state: 'upcoming',
    duration: '~30 min',
    internalKey: 'radar',
  },
  {
    num: '04',
    name: 'Creative',
    description: 'Turn your message into ads people will actually click.',
    state: 'upcoming',
    duration: '~45 min',
    internalKey: 'creative',
  },
  {
    num: '05',
    name: 'Build',
    description: 'Assemble the campaign you are about to launch.',
    state: 'upcoming',
    duration: '~30 min',
    internalKey: 'campaign-proposal',
  },
  {
    num: '06',
    name: 'Launch',
    description: 'Go live with safety guards in your own ads account.',
    state: 'upcoming',
    duration: '~20 min',
    internalKey: 'launch',
  },
  {
    num: '07',
    name: 'Signals',
    description: 'Read what is converting and where money is leaking.',
    state: 'upcoming',
    duration: 'Ongoing',
    internalKey: 'signal-loop',
  },
  {
    num: '08',
    name: 'Next test',
    description: 'Pick the smartest experiment to run next.',
    state: 'upcoming',
    duration: '~15 min',
    internalKey: 'leak-review',
  },
];

// ── Hero next-move ──────────────────────────────────────────────────────

export interface NextMove {
  stage: AcquisitionStage;
  body: string;
  ctaLabel: string;
  /** Optional context line shown next to the CTA. */
  reviewHint?: string;
}

export const ACQUISITION_NEXT_MOVE: NextMove = {
  stage: ACQUISITION_STAGES.find((s) => s.state === 'current')!,
  body:
    'Walk through the channel options for your offer, then commit to the one that fits — paid traffic, application path, organic, or another route. The product checks the trade-offs with you before you accept.',
  ctaLabel: 'Continue',
  reviewHint: 'Tom reviews this with you before you start building.',
};

// ── Total / progress helper ─────────────────────────────────────────────

export const ACQUISITION_PROGRESS = {
  total: ACQUISITION_STAGES.length,
  done: ACQUISITION_STAGES.filter((s) => s.state === 'done').length,
  currentIndex: ACQUISITION_STAGES.findIndex((s) => s.state === 'current'),
};
